import React, { memo, type ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds, USERS_INI_CALL } from '../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { globalActions } from '../../../../../../shared/store/actions';
import { useEntitlementDetails } from '../../../../hooks';
import {
  useFiltersInitialization,
  useGetSortMenu,
  useUsersColumnPicker,
} from '../hooks';
import usePageTracker from '../hooks/use-pagetracker';

import { UsersControls } from './UsersControls';
import { UsersList } from './UsersList';
import { UsersSidePanel } from './UsersSidePanel';

import './UsersTab.css';

/**
 * UsersTab Component
 *
 * Main container component for the Users tab in entitlement details.
 * Orchestrates initialization of columns and manages layout including
 * controls, list view, and sidepanel functionality with responsive design and loading states.
 *
 * @component
 * @returns {React.ReactElement} Main container with header, controls, list, and sidepanel
 */
function UsersTab(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Initial query hooks
  const { updateTracker } = (
    usePageTracker as (keys: string[]) => {
      updateTracker: (key: string, value: boolean) => void;
    }
  )(Object.values(USERS_INI_CALL));
  const entitlementDetails = useEntitlementDetails();
  const { initializeAdvancedFilters } = (
    useFiltersInitialization as () => {
      initializeAdvancedFilters: () => Promise<boolean>;
    }
  )();
  const { initializeViewColumns } = (
    useUsersColumnPicker as () => {
      initializeViewColumns: () => Promise<boolean>;
    }
  )();

  const {
    user: { isMenuPinned },
    aag: {
      details: {
        entitlementUsers: {
          sidepanel: { isSidePanelVisible },
          controls: { sortInAction },
        },
      },
      global: { isGlobalLoading },
    },
  } = useTypedSelector((state) => state);

  const userIds = useMemo(
    () => entitlementDetails.entitlementDetails?.userIds ?? [],
    [entitlementDetails.entitlementDetails?.userIds]
  );
  const entKeys = entitlementDetails.entitlementDetails?.entitlementValueKey;
  const riskKeys = useMemo(
    () => entitlementDetails.entitlementDetails?.riskIds ?? [],
    [entitlementDetails.entitlementDetails?.riskIds]
  );

  // Enable sort hook
  useGetSortMenu(sortInAction);

  // Initialize filters and columns on mount with page tracker
  useEffect(() => {
    const initializeFiltersAndColumns = async () => {
      try {
        dispatch(globalActions.setIsGlobalLoading(true));

        // Order is important
        const response: boolean[] = await Promise.all([
          initializeAdvancedFilters(),
          initializeViewColumns(),
        ]);

        // Raise global error if any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Update tracker for each initialization call
        Object.values(USERS_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      } catch (error) {
        dispatch(globalActions.setGlobalError({ state: true }));
        Object.values(USERS_INI_CALL).forEach((callValue) => {
          updateTracker(callValue, false);
        });
      } finally {
        dispatch(globalActions.setIsGlobalLoading(false));
      }
    };

    initializeFiltersAndColumns().catch(() => {
      // Error handling is already done inside the function
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const usersTabClasses = useMemo(
    () => cx('Sod-UsersTab', isMenuPinned && 'Sod-UsersTab--isMenuPinned'),
    [isMenuPinned]
  );

  const listAsideClasses = useMemo(
    () =>
      cx(
        'Sod-UsersTab-main',
        isSidePanelVisible && 'Sod-UsersTab-main--isSidePanelOpen'
      ),
    [isSidePanelVisible]
  );

  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return (
        <Box className='Sod-Violations-loaderWrapper'>
          <Loader kind='dots' format='inline' color='brand' />
        </Box>
      );
    }

    if (entitlementDetails.isError) {
      return (
        <Box className='Sod-SidePanelSummary-loaderWrapper'>
          <Typography kind='body2'>
            {l('Error loading User details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              entitlementDetails.refetch().catch(() => {
                // Error handling is managed by the hook
              });
            }}>
            {l('Retry')}
          </Button>
        </Box>
      );
    }

    return (
      <UsersList
        userIds={userIds}
        entKeys={entKeys ? [entKeys] : []}
        riskKeys={riskKeys}
      />
    );
  }, [isGlobalLoading, entitlementDetails, l, userIds, entKeys, riskKeys]);

  return (
    <Box
      className={usersTabClasses}
      dataTestId={testIds.SOD_ENTITLEMENT_TAB_USERS}>
      <Box tag='section' className={listAsideClasses}>
        {/* Controls */}
        {!isGlobalLoading &&
          !entitlementDetails.isError &&
          userIds.length > 0 && (
            <Box className='Sod-UsersTab-listAndControls'>
              <Box className='Sod-UsersTab-controlsWrapper'>
                <UsersControls />
              </Box>
            </Box>
          )}
        {/* Main scroll area */}
        <Box className='Sod-UsersTab-scrollArea'>
          {/* List */}
          {renderContent}
        </Box>
        {/* Users Sidepanel */}
        <Box className='Sod-UsersTab-asides'>
          <UsersSidePanel
            entKeys={entKeys ? [entKeys] : []}
            riskKeys={riskKeys}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(UsersTab);
